import { devLog } from '../../shared/dev/devLog';
import type {
  FiscalBuyerSyncFields,
  FiscalMode,
  SaleDiscountInput,
  SaleItemInput,
  SaleItemOptionSnapshot,
  SalePaymentInput,
  SaleStatus,
} from './salesRepoTypes';

const getPaymentStatus = (payments: SalePaymentInput[], total: number) => {
  const hasCustomer = payments.some((payment) => payment.methodId === 'fiado');
  const hasNonCustomerConfirmed = payments.some(
    (payment) => payment.methodId !== 'fiado' && (payment.status ?? 'confirmed') === 'confirmed'
  );

  if (hasCustomer && !hasNonCustomerConfirmed) return 'fiado';
  if (hasCustomer && hasNonCustomerConfirmed) return 'partial';

  const confirmedAmount = payments
    .filter((payment) => (payment.status ?? 'confirmed') === 'confirmed')
    .reduce((sum, payment) => sum + payment.amount, 0);

  if (confirmedAmount >= total) return 'paid';
  if (confirmedAmount > 0) return 'partial';
  return 'pending';
};

export type CreateSaleResult = {
  saleId: string;
  createdAt: string;
  paymentStatus: string;
  saleItemsForSync: Array<{
    itemId: string;
    productId: string;
    variantId?: string | null;
    quantity: number;
    price: number;
    unitPriceOriginal?: number;
    categoryId?: string | null;
    brandId?: string | null;
    discountAmount?: number;
    promotionId?: string | null;
    replacementUnitCost?: number;
    label?: string;
    sku?: string | null;
    itemType?: string;
    unitOfMeasure?: string | null;
    options?: SaleItemOptionSnapshot[] | null;
    lineNote?: string | null;
    performedByUserId?: string | null;
    soldByUserId?: string | null;
  }>;
  saleDiscountsForSync: Array<{
    id: string;
    promotionId?: string | null;
    label: string;
    discountAmount: number;
    scope: SaleDiscountInput['scope'];
    metadata?: Record<string, unknown>;
    appliedByUserId?: string | null;
  }>;
};

export const createSale = async (
  {
    branchId,
    total,
    subtotal,
    discountTotal,
    items,
    discounts,
    payments,
    cashSessionId,
    cashierUserId,
    fiscalMode = 'non_fiscal',
    fiscalBuyer,
    diningTableId,
    customerId,
    saleId: providedSaleId,
  }: {
    branchId: string;
    total: number;
    subtotal?: number;
    discountTotal?: number;
    items: SaleItemInput[];
    discounts?: SaleDiscountInput[];
    payments: SalePaymentInput[];
    cashSessionId?: string | null;
    cashierUserId?: string | null;
    fiscalMode?: FiscalMode;
    fiscalBuyer?: FiscalBuyerSyncFields | null;
    diningTableId?: string | null;
    customerId?: string | null;
    saleDraftId?: string | null;
    /** Idempotency: if provided, reuses this saleId instead of generating a new one. */
    saleId?: string;
  }
): Promise<CreateSaleResult> => {
  const saleId = providedSaleId ?? crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const paymentStatus = getPaymentStatus(payments, total);
  const fiscalStatus = fiscalMode === 'non_fiscal' ? 'not_required' : 'pending_sync';

  const saleItemsForSync: Array<{
    itemId: string;
    productId: string;
    variantId?: string | null;
    quantity: number;
    price: number;
    unitPriceOriginal?: number;
    categoryId?: string | null;
    brandId?: string | null;
    discountAmount?: number;
    promotionId?: string | null;
    replacementUnitCost?: number;
    label?: string;
    sku?: string;
    itemType?: string;
    unitOfMeasure?: string | null;
    options?: SaleItemInput['options'];
    lineNote?: string | null;
    performedByUserId?: string | null;
    soldByUserId?: string | null;
  }> = [];

  const saleDiscountsForSync: Array<{
    id: string;
    promotionId?: string | null;
    label: string;
    discountAmount: number;
    scope: SaleDiscountInput['scope'];
    metadata?: Record<string, unknown>;
    appliedByUserId?: string | null;
  }> = [];

  // Web sin OPFS: no INSERT local; el server aplica vía ingest.
  for (const item of items) {
          const itemId = crypto.randomUUID();
          const lineNote = item.lineNote?.trim() || null;
          saleItemsForSync.push({
            itemId,
            productId: item.productId,
            variantId: item.variantId ?? null,
            quantity: item.quantity,
            price: item.price,
            unitPriceOriginal: item.unitPriceOriginal ?? item.price,
            categoryId: item.categoryId ?? null,
            brandId: item.brandId ?? null,
            discountAmount: item.discountAmount ?? 0,
            promotionId: item.promotionId ?? null,
            label: item.name?.trim() || undefined,
            sku: item.sku,
            itemType: item.itemType ?? (item.affectsStock === false ? 'custom' : 'product'),
            unitOfMeasure: item.unitOfMeasure ?? null,
            options: item.options?.length ? item.options : undefined,
            lineNote,
            performedByUserId: item.performedByUserId ?? null,
            soldByUserId: item.soldByUserId ?? null,
          });
        }
        for (const discount of discounts ?? []) {
          const discountId = discount.id ?? crypto.randomUUID();
          saleDiscountsForSync.push({
            id: discountId,
            promotionId: discount.promotionId ?? null,
            label: discount.label,
            discountAmount: discount.discountAmount,
            scope: discount.scope,
            metadata: discount.metadata,
            appliedByUserId: discount.appliedByUserId ?? cashierUserId ?? null,
          });
        }
        for (const payment of payments) {
          payment.id = payment.id ?? crypto.randomUUID();
        }
        return { saleId, createdAt, paymentStatus, saleItemsForSync, saleDiscountsForSync };
};

const normalizeSaleStatus = (raw: string | null | undefined): SaleStatus =>
  raw === 'voided' ? 'voided' : 'active';

const inferAffectsStockFromProduct = (row: {
  item_type: string | null;
  is_misc: number | null;
}): boolean => {
  const itemType = (row.item_type ?? '').toLowerCase();
  if (itemType === 'service') return false;
  return !(row.is_misc === 1);
};

export type VoidSaleResult = {
  saleId: string;
  voidedAt: string;
  voidedByUserId: string | null;
  voidReason: string;
  fiscalMode: string | null;
  cashSessionId: string | null;
  branchId: string;
};
